import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { setNotification } from "features/Notification/notificationSlice";
import authHeader from "services/auth-header";
import API from "services/axios";
import useEfsInvoice from "../EfsInvoiceContext";
import { Modal } from "react-bootstrap";
import { PDFViewer } from "@react-pdf/renderer";
import { EfsInvoicePdf } from "../EfsInvoicePdf";

export default function EfsInvDetails({ invId }) {
  /* ******************** All States ************************* */
  const [formData, setFormData] = useState(null);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const { selectedInv, GetInvById } = useEfsInvoice();

  const [showPdfModal, setShowPdfModal] = useState(false);
  const [selectedItemForPdf, setSelectedItemForPdf] = useState(null);

  /* ******************** All Functions ************************* */
  const headerData = formData || {};

  const handleChange = (field) => (e) => {
    const { value } = e.target;
    setFormData((prev) => ({ ...(prev || {}), [field]: value }));
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const payload = { ...(formData || {}), id: selectedInv.id };
      const { data: resp } = await API.patch(
        "/manufacturer/efs-invoice",
        payload,
        { headers: authHeader() }
      );

      if (resp?.success) {
        GetInvById && (await GetInvById(selectedInv.id));
        dispatch(
          setNotification({
            message: "Invoice updated successfully",
            type: "success",
          })
        );
      } else {
        dispatch(
          setNotification({
            message: resp?.message || "Failed to update invoice",
            type: "error",
          })
        );
      }
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  /* ******************** All UseEffects ************************* */
  useEffect(() => {
    if (!formData && selectedInv) {
      setFormData(selectedInv);
    }
  }, [selectedInv, formData]);

  useEffect(() => {
    if (invId) {
      GetInvById && GetInvById(invId);
    }
  }, [invId]);

  if (!selectedInv) {
    return null;
  }

  return (
    <div className="efs-inv-details container-fluid">
      <form onSubmit={handleUpdate}>
        <div className="bg-white d-flex justify-content-between align-items-center px-3 py-2 rounded-3 mb-2 shadow-sm">
          <div>
            <h5 className="">EFS Invoice Details</h5>
          </div>
          <div>
            <button
              type="button"
              className="btn btn-info btn-sm me-2"
              onClick={() => {
                setSelectedItemForPdf(selectedInv);
                setShowPdfModal(true);
              }}
            >
              <i className="mdi mdi-file-pdf-box me-1"></i> PDF
            </button>
            <button
              type="submit"
              className="btn btn-primary btn-sm"
              disabled={loading}
            >
              {loading ? "Updating..." : "Update"}
            </button>
          </div>
        </div>
        <div className="table-responsive bg-white shadow-sm p-3 rounded-3">
          <table className="table table-bordered border-dark mb-0 efs-inv-header-table">
            <tbody>
              <tr>
                <td className="fw-bold w-25">INVOICE NO:</td>
                <td>
                  <input
                    type="text"
                    className="form-control form-control-sm border-0 border-bottom border-1 rounded-0"
                    value={headerData.invoiceNo || ""}
                    onChange={handleChange("invoiceNo")}
                  />
                </td>
                <td>
                  <input
                    type="date"
                    className="form-control form-control-sm border-0 border-bottom border-1 rounded-0"
                    value={headerData.invoiceDate || ""}
                    onChange={handleChange("invoiceDate")}
                  />
                </td>
                <td
                  rowSpan="7"
                  className="align-top border-start border-dark h-100"
                  style={{ width: "30%" }}
                >
                  <div className="fw-bold mb-2">CONSIGNEE:</div>
                  <div className="mb-1">
                    <div>BILL TO.</div>
                    <input
                      type="text"
                      className="form-control form-control-sm border-0 border-bottom border-1 rounded-0 p-0"
                      value={headerData.billTo || ""}
                      onChange={handleChange("billTo")}
                    />
                  </div>
                  <div className="mb-1">
                    <div>SHIP TO.</div>
                    <input
                      type="text"
                      className="form-control form-control-sm border-0 border-bottom border-1 rounded-0 p-0"
                      value={headerData.shipTo || ""}
                      onChange={handleChange("shipTo")}
                    />
                  </div>
                  <div className="mb-1">
                    <input
                      type="text"
                      className="form-control form-control-sm border-0 border-bottom border-1 rounded-0 p-0"
                      value={headerData.shipAddress || ""}
                      onChange={handleChange("shipAddress")}
                    />
                  </div>
                  <div className="mb-1">
                    <input
                      type="text"
                      className="form-control form-control-sm border-0 border-bottom border-1 rounded-0 p-0"
                      value={headerData.postalCode || ""}
                      onChange={handleChange("postalCode")}
                    />
                  </div>
                  <div className="mb-1">
                    <div>TEL No.</div>
                    <input
                      type="text"
                      className="form-control form-control-sm border-0 border-bottom border-1 rounded-0 p-0"
                      value={headerData.phone || ""}
                      onChange={handleChange("phone")}
                    />
                  </div>
                  <div className="flex-center flex-column">
                    <div className="mt-4">
                      R &nbsp; 1-{headerData.noOfPackages || ""} &nbsp; I
                    </div>
                    <div>{headerData.destination || ""}</div>
                  </div>
                </td>
              </tr>
              <tr>
                <td className="fw-bold w-25">INVOICE OF:</td>
                <td colSpan="2">
                  <input
                    type="text"
                    className="form-control form-control-sm border-0 border-bottom border-1 rounded-0"
                    value={headerData.invoiceOf || ""}
                    onChange={handleChange("invoiceOf")}
                  />
                </td>
              </tr>
              <tr>
                <td className="fw-bold w-25">NO. &amp; KIND OF PACKAGES:</td>
                <td colSpan="2">
                  <div className="d-flex align-items-center">
                    <input
                      type="text"
                      className="form-control form-control-sm border-0 border-bottom border-1 rounded-0 me-1"
                      value={headerData.noOfPackages || ""}
                      onChange={handleChange("noOfPackages")}
                      style={{ maxWidth: "80px" }}
                    />
                    <span>CARTONS</span>
                  </div>
                </td>
              </tr>
              <tr>
                <td className="fw-bold w-25">SHIPMENT:</td>
                <td colSpan="2">
                  <input
                    type="text"
                    className="form-control form-control-sm border-0 border-bottom border-1 rounded-0 text-uppercase"
                    value={headerData.shipmentType || ""}
                    onChange={handleChange("shipmentType")}
                  />
                </td>
              </tr>
              <tr>
                <td className="fw-bold w-25">DESTINATION:</td>
                <td colSpan="2">
                  <input
                    type="text"
                    className="form-control form-control-sm border-0 border-bottom border-1 rounded-0"
                    value={headerData.destination || ""}
                    onChange={handleChange("destination")}
                  />
                </td>
              </tr>
              <tr>
                <td className="fw-bold w-25">TERMS OF PAYMENT:</td>
                <td colSpan="2">
                  <input
                    type="text"
                    className="form-control form-control-sm border-0 border-bottom border-1 rounded-0"
                    value={headerData.termsOfPayment || ""}
                    onChange={handleChange("termsOfPayment")}
                  />
                </td>
              </tr>
              <tr>
                <td className="fw-bold w-25">
                  FINANCIAL INSTRUMENT NO. &amp; DATE:
                </td>
                <td>
                  <input
                    type="text"
                    className="form-control form-control-sm border-0 border-bottom border-1 rounded-0"
                    value={headerData.financialInstrument || ""}
                    onChange={handleChange("financialInstrument")}
                  />
                </td>
                <td>
                  <input
                    type="date"
                    className="form-control form-control-sm border-0 border-bottom border-1 rounded-0"
                    value={headerData.financialInstrumentDate || ""}
                    onChange={handleChange("financialInstrumentDate")}
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </form>
      {/*   PDF Modal
       ********************************************* */}
      <Modal
        show={showPdfModal}
        size="xl"
        onHide={() => {
          setShowPdfModal(false);
          setSelectedItemForPdf(null);
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Invoice PDF - {selectedItemForPdf?.invoiceNo || "N/A"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0" style={{ height: "80vh" }}>
          {selectedItemForPdf && (
            <PDFViewer
              style={{ width: "100%", height: "100%", border: "none" }}
            >
              <EfsInvoicePdf
                data={selectedItemForPdf}
                efsNo={"EFS-SEXP-3926897-228-19-03-2023"}
              />
            </PDFViewer>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
}
